import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import constants from '../../../constants'
import PublicMainContainer from '../../../layouts/public/PublicMainContainer'
import useLeaveRequestMedicalReimbursementPageViewModel from './viewModel'
import { Controller } from 'react-hook-form'
import { ReimbursementType } from '../../../database/enums/ReimbursementType.enum'
import ReimbursementTypeEnumHelper from '../../../database/enums/helpers/reimbursement-type.helper'
import { DatePicker } from '@mui/x-date-pickers'
import VisuallyHiddenInput from '../../../components/ui/VisuallyHiddenInput'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import CurrencyEnumHelper from '../../../database/enums/helpers/currency.helper'
import { Currency } from '../../../database/enums/Currency.enum'

const scopeNamePrefix = 'public-medical-reimbursement'

export default function LeaveRequestMedicalReimbursementPage() {
  const theme = useTheme()
  const viewModel = useLeaveRequestMedicalReimbursementPageViewModel()

  const getFileUploadedPreviewLabel = (value?: File | null) => {
    if (!!value) return `${value.name} (${Intl.NumberFormat().format(value.size / (1024 * 1024))}MB)`
    return ''
  }

  return (
    <PublicMainContainer
      showBackButton
      wrapperBoxSx={{ bgcolor: constants.colors.neutrals.solitude, minHeight: '92vh', pt: 4, px: 2, pb: 16 }}
    >
      <Box sx={{ width: '100%', maxWidth: theme.breakpoints.values.sm, mx: 'auto' }}>
        {viewModel.addDataStatus.isError ? (
          <Alert severity='error' variant='standard' elevation={5} sx={{ mb: 2 }}>
            <AlertTitle>Error!</AlertTitle>
            Please kindly try again later...
          </Alert>
        ) : null}
        {viewModel.addDataStatus.isSuccess ? (
          <Card elevation={5}>
            <CardContent sx={{ p: 0, height: 12, bgcolor: constants.colors.neutrals.black }} />
            <CardHeader
              title={'Thank You :)'}
              subheader={"Your reimbursement request has been submitted.\nYou'll get an email once it's processed."}
              slotProps={{ title: { variant: 'h4' }, subheader: { variant: 'body1', whiteSpace: 'pre-wrap' } }}
            />
            <Divider />
            <CardContent>
              <Typography
                variant='subtitle2'
                color='primary.main'
                onClick={viewModel.resetPage}
                sx={{ cursor: 'pointer' }}
              >
                {'Submit another response.'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <form onSubmit={viewModel.formHook.handleSubmit(viewModel.onSubmit)}>
            <Card elevation={5}>
              <CardContent sx={{ p: 0, height: 12, bgcolor: constants.colors.neutrals.black }} />
              <CardHeader
                title={'Medical Reimbursement Form'}
                subheader={'Please complete this form to request reimbursement for medical expenses.'}
                slotProps={{ title: { variant: 'h4' }, subheader: { variant: 'subtitle2' } }}
              />
              <CardContent>
                <Typography variant='subtitle2' color='error.main'>
                  {'* Indicates required'}
                </Typography>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='name'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Name'
                        placeholder='Enter your name...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='email'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Email'
                        placeholder='Enter your email...'
                        type='email'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='reimbursementType'
                    render={({ field }) => (
                      <FormControl fullWidth required>
                        <InputLabel id={`${scopeNamePrefix}-reimbursementType-label`}>Reimbursement Type</InputLabel>
                        <Select
                          labelId={`${scopeNamePrefix}-reimbursementType-label`}
                          label='Type of reimbursement'
                          {...field}
                        >
                          {Object.values(ReimbursementType).map((item, index) => (
                            <MenuItem key={`${scopeNamePrefix}-reimbursementType-option-${index}`} value={item}>
                              {ReimbursementTypeEnumHelper.getLabel(item)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='serviceDate'
                    render={({ field, fieldState }) => (
                      <DatePicker
                        label='Date of Service'
                        {...field}
                        slotProps={{
                          textField: {
                            required: true,
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                        sx={{ flex: 1 }}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='vendor'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Vendor'
                        placeholder='Enter the vendor name...'
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='description'
                    render={({ field }) => (
                      <TextField
                        variant='outlined'
                        label='Description'
                        placeholder='Enter the description...'
                        multiline
                        minRows={3}
                        {...field}
                      />
                    )}
                  />
                </Stack>
              </CardContent>
              <Divider />
              <CardContent>
                <Stack gap={2}>
                  <Controller
                    control={viewModel.formHook.control}
                    name='totalAmountClaimed.value'
                    render={({ field, fieldState }) => (
                      <TextField
                        variant='outlined'
                        required
                        label='Total Amount Claimed'
                        placeholder='Enter the amount...'
                        slotProps={{
                          input: {
                            startAdornment: (
                              <Controller
                                control={viewModel.formHook.control}
                                name='totalAmountClaimed.currency'
                                render={totalAmountClaimedCurrencyForm => (
                                  <InputAdornment position='start'>
                                    {CurrencyEnumHelper.getSymbol(
                                      totalAmountClaimedCurrencyForm.field.value as Currency,
                                    )}
                                  </InputAdornment>
                                )}
                              />
                            ),
                          },
                        }}
                        {...field}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  <Controller
                    control={viewModel.formHook.control}
                    name='supportingDocument'
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth required error={!!fieldState.error}>
                        <FormLabel sx={{ mb: 2 }}>
                          <Typography mb={2}>Upload supporting documents (e.g., receipts, Invoice)</Typography>
                          <Typography variant='caption'>Upload 1 supported file: PDF or image. Max 1 GB.</Typography>
                        </FormLabel>
                        <Stack direction={'row'} gap={2} alignItems={'center'} flexWrap={'wrap'}>
                          <Button
                            component='label'
                            role={undefined}
                            variant='contained'
                            tabIndex={-1}
                            startIcon={<FileUploadIcon />}
                          >
                            Add File
                            <VisuallyHiddenInput
                              type='file'
                              accept='.pdf, image/*'
                              onChange={e => {
                                if (!!e.target.files && !!e.target.files[0]) field.onChange(e.target.files[0])
                              }}
                            />
                          </Button>
                          <Typography variant='subtitle2' color='primary.main'>
                            {getFileUploadedPreviewLabel(field.value)}
                          </Typography>
                        </Stack>
                      </FormControl>
                    )}
                  />
                </Stack>
              </CardContent>
            </Card>
            <Stack direction='row' gap={2} alignItems={'center'} justifyContent={'space-between'} mt={2}>
              <Button
                type='submit'
                size='large'
                variant='contained'
                disabled={viewModel.addDataStatus.isLoading}
                loading={viewModel.addDataStatus.isLoading}
              >
                Submit
              </Button>
              <Button type='reset' size='large' variant='text' onClick={() => viewModel.formHook.reset()}>
                Clear form
              </Button>
            </Stack>
          </form>
        )}
      </Box>
    </PublicMainContainer>
  )
}
